import React, { useContext, useEffect } from 'react';
import ReactGA from 'react-ga4';
import { ChakraProvider } from '@chakra-ui/react';
import CustomSpinner from './components/common/Spinner';
import PublicRoute from './components/routes/public-route';
import PrivateRoute from './components/routes/private-route';
import RestrictedRoute from './components/routes/restricted-route';
// import lazysizes to handle images lazyload https://github.com/aFarkas/lazysizes
import 'lazysizes';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorPage from './pages/courtesy-pages/error';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import Fonts from './theme/fonts';
import { mainTheme } from './styles/mainTheme';
import { useRouteHelper } from './hooks/routesHelpers';
import routes from './config/routes';
import Page404 from './pages/courtesy-pages/page404';
import ScrollToTop from './helpers/ScrollToTop';
import { SessionContext } from './contexts/SessionContext';
import { PropertyInfoContext } from './contexts/PropertyInfoContext';
import { SessionReducerActionOptions } from './reducers/SessionReducer';
import { PropertyInfoReducerActionOptions } from './reducers/PropertyInfoReducer';
import { PropertyInfoContextDto } from './contexts/dtos/PropertyInfoContextDto';
import { fromStringToTicks } from './utils/dateUtils';
import AdminRoute from './components/routes/admin-route';
import CookieBannerModal from './components/interfaces/common/CookieBannerModal';
import { MapProvider } from './contexts/MapContext';
import { useDetectIsConfirmEmailPage } from './helpers/getIsLandingPage';
import RouteChangeTracker from './components/routes/RouteChangeTracker';

function AppRouting() {
  const { getRoute } = useRouteHelper();
  const history = useHistory();
  const isConfirmEmailPage = useDetectIsConfirmEmailPage();
  const { state, dispatch } = useContext(SessionContext);
  const { dispatch: propertyInfoDispatch } = useContext(PropertyInfoContext);

  const handleLogOut = () => {
    dispatch({ type: SessionReducerActionOptions.Reset, session: null });
    propertyInfoDispatch({ type: PropertyInfoReducerActionOptions.Update, propertyInfo: new PropertyInfoContextDto() });
  };

  if (state.isLogged() && state.expiredAt < fromStringToTicks(Date().toString())) {
    handleLogOut();
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorPage}
      onReset={() => {
        history.push(getRoute('/')); // always redirect to hp
      }}
    >
      <Switch>
        {/* redirect to HP on landing */}
        <Redirect exact from="/" to={getRoute('home')} />
        {/* public routes */}
        {routes
          .filter((route) => route.routeStatus === 'public')
          .map((route) => (
            <PublicRoute
              key={route.key}
              // @ts-ignore
              exact={route.isExact}
              path={getRoute(route.key)}
              component={route.component}
            />
          ))}
        {/* private routes */}
        {routes
          .filter((route) => route.routeStatus === 'private')
          .map((route) => (
            <PrivateRoute
              key={route.key}
              // @ts-ignore
              exact={route.isExact}
              path={getRoute(route.key)}
              component={route.component}
            />
          ))}
        {/* restricted routes */}
        {routes
          .filter((route) => route.routeStatus === 'restricted')
          .map((route) => (
            <RestrictedRoute
              key={route.key}
              // @ts-ignore
              exact={route.isExact}
              path={getRoute(route.key)}
              component={route.component}
            />
          ))}
        {routes
          .filter((route) => route.routeStatus === 'admin')
          .map((route) => (
            <AdminRoute
              key={route.key}
              // @ts-ignore
              exact={route.isExact}
              path={getRoute(route.key)}
              component={route.component}
            />
          ))}
        <Route>
          <Page404 />
        </Route>
      </Switch>
      <RouteChangeTracker />
      {!isConfirmEmailPage && <CookieBannerModal />}
    </ErrorBoundary>
  );
}

function App() {
  const TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID;
  const isProduction = process.env.NODE_ENV === 'production';

  // Initialize GA if not done already
  useEffect(() => {
    if (TRACKING_ID && isProduction) {
      ReactGA.initialize(TRACKING_ID);
    }
  }, [TRACKING_ID, isProduction]);

  return (
    <React.Suspense fallback={<CustomSpinner />}>
      <ChakraProvider theme={mainTheme}>
        <Fonts />
        <ScrollToTop />
        <MapProvider>
          <AppRouting />
        </MapProvider>
      </ChakraProvider>
    </React.Suspense>
  );
}

export default App;
